import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip,
  useTheme,
  alpha,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  TaskAlt as TaskSidebarIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  BarChart as ProgressIcon,
  ErrorOutline as ErrorIcon,
  ViewList as ViewListIcon,
  ViewKanban as ViewKanbanIcon,
  PendingActions as PendingIcon,
  RateReview as ReviewIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { TASK_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ListFilterBar from '../../common/ListFilterBar';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import FeedbackMessage from '../../common/FeedbackMessage';
import { SUCCESS, ACCENT, TABLE_SURFACE_2 } from '../../common/SaaSTokens';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canCreateTask,
  canArchiveTask,
  canRestoreTask,
  canEditTask,
} from '../../../utils/taskPermissions';
import { motion } from 'framer-motion';
import TaskKanban from './TaskKanban';
import {
  TASK_STATUS_FILTER_OPTIONS,
  resolveTaskStatus,
  taskMatchesStatusFilter,
  getTaskStatusMeta,
} from '../../../utils/taskStatus';

// ─── Design tokens — same as the rest of the system ──────────────────────────
const B = {
  50:  '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#60A5FA',
  500: '#3B82F6',
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1E40AF',
  900: '#1E3A8A',
};

const TASK_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes task-shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
`;

// ─── Progress bar — design-system styled ─────────────────────────────────────
const ProgressBar = ({ value }) => {
  const color =
    value >= 80 ? '#10b981'
  : value >= 40 ? '#f59e0b'
  : '#ef4444';

  return (
    <Box sx={{ width: '100%', minWidth: 100 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
        <Typography sx={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.72rem', fontWeight: 700,
          color: color,
        }}>
          {value}%
        </Typography>
      </Box>
      <Box sx={{
        height: 6, borderRadius: '10px',
        background: `rgba(191,219,254,0.4)`,
        overflow: 'hidden',
        border: `1px solid rgba(191,219,254,0.5)`,
      }}>
        <Box sx={{
          height: '100%',
          width: `${value}%`,
          borderRadius: '10px',
          background: value >= 80
            ? 'linear-gradient(90deg,#10b981,#059669)'
            : value >= 40
            ? 'linear-gradient(90deg,#f59e0b,#d97706)'
            : 'linear-gradient(90deg,#ef4444,#dc2626)',
          transition: 'width 0.5s ease',
          boxShadow: `0 0 8px ${value >= 80 ? 'rgba(16,185,129,0.4)' : value >= 40 ? 'rgba(245,158,11,0.35)' : 'rgba(239,68,68,0.35)'}`,
        }} />
      </Box>
    </Box>
  );
};

const STATUS_CHIP_ICONS = {
  completed: <CheckCircleIcon />,
  in_progress: <TrendingUpIcon />,
  review: <ProgressIcon />,
  not_started: <ScheduleIcon />,
};

// ─── Status chip — design-system spec ────────────────────────────────────────
const StatusChip = ({ task }) => {
  const statusId = resolveTaskStatus(task);
  const meta = getTaskStatusMeta(statusId);
  const icon = STATUS_CHIP_ICONS[statusId] ?? STATUS_CHIP_ICONS.not_started;
  const v = {
    label: meta.label,
    icon,
    bg: `linear-gradient(135deg, ${meta.color}, ${alpha(meta.color, 0.75)})`,
    border: meta.border,
    shadow: alpha(meta.color, 0.3),
  };

  return (
    <Chip
      icon={React.cloneElement(v.icon, { sx: { fontSize: '13px !important', color: '#fff !important' } })}
      label={v.label}
      size="small"
      sx={{
        height: 24, fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.64rem', fontWeight: 600, letterSpacing: 0.4,
        background: v.bg, color: '#fff',
        border: `1px solid ${v.border}`,
        boxShadow: `0 0 8px ${v.shadow}`,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

// ─── Department chip ──────────────────────────────────────────────────────────
const DeptChip = ({ label }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      height: 22, fontFamily: "'JetBrains Mono', monospace",
      fontSize: '0.62rem', fontWeight: 600,
      bgcolor: `rgba(219,234,254,0.8)`, color: B[700],
      border: `1px solid rgba(147,197,253,0.5)`,
      '& .MuiChip-label': { px: 1 },
    }}
  />
);

// ─── User avatar ──────────────────────────────────────────────────────────────
const UserAvatar = ({ user }) => (
  <Tooltip title={user.name} arrow
    componentsProps={{ tooltip: { sx: { fontFamily: "'DM Sans', sans-serif", bgcolor: 'rgba(15,23,42,0.9)', borderRadius: '8px' } } }}
  >
    <Avatar
      sx={{
        width: 30, height: 30,
        background: `linear-gradient(135deg, ${B[400]}, ${B[700]})`,
        border: `2px solid rgba(147,197,253,0.5)`,
        boxShadow: `0 0 8px rgba(59,130,246,0.2)`,
        fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
        fontSize: '0.82rem', color: '#fff',
      }}
    >
      {user.name.charAt(0).toUpperCase()}
    </Avatar>
  </Tooltip>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const TaskList = () => {
  const [tasks,          setTasks]          = useState([]);
  const [filteredTasks,  setFilteredTasks]  = useState([]);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [showFilters,    setShowFilters]    = useState(false);
  const [filters,        setFilters]        = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [departments,    setDepartments]    = useState([]);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveLoading,   setArchiveLoading]   = useState(false);
  const [taskToArchive,   setTaskToArchive]   = useState(null);
  const [error,          setError]          = useState(null);
  const [success,        setSuccess]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [page,           setPage]           = useState(0);
  const [rowsPerPage,    setRowsPerPage]    = useState(10);
  const [viewMode,       setViewMode]       = useState(() => {
    try {
      return localStorage.getItem('taskListView') === 'kanban' ? 'kanban' : 'table';
    } catch {
      return 'table';
    }
  });
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  const latestFetchIdRef = useRef(0);

  const theme    = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();


  const normalizeTask = (task) => ({
    ...task,
    description: task?.description || '',
    departments: Array.isArray(task?.departments) ? task.departments : [],
    users: Array.isArray(task?.users) ? task.users : [],
    progress: Number.isFinite(Number(task?.progress)) ? Number(task.progress) : 0,
    status: resolveTaskStatus(task),
  });

  // Inject CSS once
  useEffect(() => {
    const cssId = 'task-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = TASK_CSS;
      document.head.appendChild(el);
    }
  }, []);

  // ── All original logic — UNCHANGED ────────────────────────────────────────

  const filterOptions = [
    {
      name: 'record_state', label: 'Record State', type: 'select',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      name: 'department', label: 'Department', type: 'select',
      options: departments.map(dept => ({ value: dept.id.toString(), label: dept.name })),
    },
    {
      name: 'status', label: 'Status', type: 'select',
      options: TASK_STATUS_FILTER_OPTIONS,
    },
    {
      name: 'progress', label: 'Progress Range', type: 'select',
      options: [
        { value: '0-25',   label: '0–25%'   },
        { value: '26-50',  label: '26–50%'  },
        { value: '51-75',  label: '51–75%'  },
        { value: '76-100', label: '76–100%' },
      ],
    },
  ];

  useEffect(() => {
    fetchTasks({ forceRefresh: Boolean(location.state?.refreshList) });
    fetchDepartments();
  }, [location.pathname, location.state?.refreshList]);

  const fetchTasks = async ({ forceRefresh = false, archived = false } = {}) => {
    const fetchId = ++latestFetchIdRef.current;
    try {
      setLoading(true);
      const response = await api.get('/tasks', {
        ...(forceRefresh ? { cache: false } : {}),
        params: { per_page: 200, ...(archived ? { archived: 1 } : {}) },
      });
      const payload = response?.data;
      const rawRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      const rows = rawRows.map(normalizeTask);
      if (fetchId !== latestFetchIdRef.current) return;
      setTasks(rows);
      setFilteredTasks(rows);
      // Fallback for users who cannot access /departments endpoint:
      // derive filter options from departments attached to visible tasks.
      setDepartments((prev) => {
        if (prev.length > 0) return prev;
        const map = new Map();
        rows.forEach((task) => {
          (task?.departments || []).forEach((dept) => {
            if (dept?.id != null && dept?.name) {
              map.set(Number(dept.id), { id: Number(dept.id), name: dept.name });
            }
          });
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
      });
      setError(null);
    } catch (error) {
      if (fetchId !== latestFetchIdRef.current) return;
      // Keep showing existing tasks when a background refresh fails.
      // This avoids showing an error banner together with valid list data.
      if ((tasks?.length || 0) === 0) {
        setError('Failed to load tasks. Please try again.');
      }
    } finally {
      if (fetchId === latestFetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments?minimal=1');
      setDepartments(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      // Some roles cannot access /departments; task list can still work with fallback departments from /tasks.
      // Keep this silent to avoid intermittent confusing error banners.
    }
  };

  useEffect(() => {
    let filtered = tasks;
    if (searchTerm) {
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.users.some(u => (u?.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        switch (key) {
          case 'department':
            filtered = filtered.filter(task => task.departments.some(dept => Number(dept?.id) === parseInt(value)));
            break;
          case 'status':
            filtered = filtered.filter((task) => taskMatchesStatusFilter(task, value));
            break;
          case 'progress':
            filtered = filtered.filter(task => {
              const [min, max] = value.split('-').map(Number);
              return task.progress >= min && task.progress <= max;
            });
            break;
          case 'record_state':
            break;
          default: break;
        }
      }
    });
    filtered = [...filtered].sort((a, b) => {
      const rank = (task) => (resolveTaskStatus(task) === 'completed' ? 1 : 0);
      return rank(a) - rank(b);
    });
    setFilteredTasks(filtered);
  }, [searchTerm, filters, tasks]);

  const handleFilterChange  = (newFilters) => {
    const archivedViewChanged =
      (filters.record_state === 'archived') !== (newFilters.record_state === 'archived');
    const nextActive =
      newFilters.status === 'completed' ? 'completed' :
      newFilters.status === 'review' ? 'review' :
      newFilters.status === 'in_progress' ? 'in_progress' :
      newFilters.status === 'not_started' ? 'not_started' :
      'all';
    setActiveStatFilter(nextActive);
    setFilters(newFilters);
    setPage(0);
    clearSelection();
    if (archivedViewChanged) {
      if (newFilters.record_state === 'archived') {
        setViewMode('table');
      }
      fetchTasks({ forceRefresh: true, archived: newFilters.record_state === 'archived' });
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('taskListView', mode);
    } catch {
      /* ignore */
    }
  };

  const handleKanbanStatusChange = async (task, newStatus) => {
    const prevTasks = tasks;
    const optimistic = prevTasks.map((t) =>
      t.id === task.id ? { ...t, status: newStatus, progress: newStatus === 'completed' ? 100 : newStatus === 'not_started' ? 0 : t.progress } : t
    );
    setTasks(optimistic);
    setUpdatingTaskId(task.id);
    try {
      const { data } = await api.patch(`/tasks/${task.id}/status`, { status: newStatus });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? normalizeTask(data) : t)));
      setSuccess(`Moved "${task.title}" to ${getTaskStatusMeta(newStatus).label}`);
    } catch (err) {
      setTasks(prevTasks);
      setError(err.response?.data?.message || 'Failed to update task status');
    } finally {
      setUpdatingTaskId(null);
    }
  };
  const handleClearFilters  = () => {
    const wasArchived = filters.record_state === 'archived';
    setFilters({});
    setSearchTerm('');
    setActiveStatFilter('all');
    setPage(0);
    clearSelection();
    if (wasArchived) {
      fetchTasks({ forceRefresh: true });
    }
  };

  const handleArchiveClick = (task) => {
    if (!canArchiveTask(user, task)) {
      setError('Only team leaders or the assigned engineering department head can archive this task.');
      return;
    }
    setTaskToArchive(task);
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    try {
      setArchiveLoading(true);
      await api.post(`/tasks/${taskToArchive.id}/archive`);
      setTasks(tasks.filter(t => t.id !== taskToArchive.id));
      setArchiveDialogOpen(false);
      setTaskToArchive(null);
      setSuccess(`Task "${taskToArchive.title}" has been archived successfully`);
    } catch (error) {
      setError(error.response?.data?.message || 'Error archiving task');
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    const ids = [...(selectedIds || [])];
    if (!ids.length) {
      setError('No selected tasks can be archived.');
      return;
    }

    const results = await Promise.allSettled(
      ids.map((taskId) => api.post(`/tasks/${taskId}/archive`)),
    );
    const failed = results.filter((result) => result.status === 'rejected');
    const archivedCount = ids.length - failed.length;

    if (archivedCount > 0) {
      const idSet = new Set(ids.map(String));
      setTasks((prev) => prev.filter((task) => !idSet.has(String(task.id))));
      setSuccess(`${archivedCount} task(s) archived successfully.`);
      clearSelection();
      fetchTasks({ forceRefresh: true });
    }

    if (failed.length > 0) {
      const firstError = failed[0]?.reason;
      const fallback =
        firstError?.response?.data?.message
        || Object.values(firstError?.response?.data?.errors || {}).flat()?.[0]
        || 'Archive failed.';
      setError(
        archivedCount > 0
          ? `${archivedCount} of ${ids.length} archived. ${failed.length} could not be processed. ${fallback}`
          : fallback,
      );
    } else {
      setError(null);
    }
  };

  const handleRestore = async (task) => {
    if (!canRestoreTask(user, task)) {
      setError('Only team leaders or the assigned engineering department head can restore this task.');
      return;
    }
    try {
      await api.post(`/tasks/${task.id}/restore`);
      setTasks((prev) => prev.filter((item) => item.id !== task.id));
      setSuccess(`Task "${task.title}" has been restored successfully`);
    } catch (error) {
      setError(error.response?.data?.message || 'Error restoring task');
    }
  };

  const handleChangePage       = (event, newPage)  => { setPage(newPage); };
  const handleChangeRowsPerPage = (event)           => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  // ── Stat cards — CALCULATED FROM ALL DATA (NOT FILTERED) ──────────────────
  const countByStatus = (statusId) =>
    tasks.filter((t) => resolveTaskStatus(t) === statusId).length;

  const statsData = [
    { icon: AssignmentIcon,  value: tasks.length,                  label: 'Total',       color: 'info'      },
    { icon: PendingIcon,     value: countByStatus('not_started'),  label: 'To Do',       color: 'primary'   },
    { icon: TrendingUpIcon,  value: countByStatus('in_progress'),  label: 'In Progress', color: 'warning'   },
    { icon: ReviewIcon,      value: countByStatus('review'),       label: 'Review',      color: 'secondary' },
    { icon: CheckCircleIcon, value: countByStatus('completed'),    label: 'Done',        color: 'success'   },
  ];

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'not_started', 'in_progress', 'review', 'completed'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.status;
    } else {
      nextFilters.status = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'not_started' ? 1 :
    activeStatFilter === 'in_progress' ? 2 :
    activeStatFilter === 'review'      ? 3 :
    activeStatFilter === 'completed'   ? 4 :
    0;

  const columns = [
    {
      key: 'title',
      label: 'Task Title',
      icon: DescriptionIcon,
      minWidth: 220,
      render: (task) => (
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#fff', mb: 0.3 }}>
            {task.title}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {task.description?.substring(0, 60)}...
          </Typography>
        </Box>
      ),
    },
    {
      key: 'departments',
      label: 'Departments',
      icon: BusinessIcon,
      minWidth: 160,
      render: (task) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {task.departments?.slice(0, 2).map(dept => (
            <DeptChip key={dept.id} label={dept.name} />
          ))}
          {task.departments?.length > 2 && (
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', pt: 0.3 }}>
              +{task.departments.length - 2}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'progress',
      label: 'Progress',
      icon: ProgressIcon,
      minWidth: 160,
      render: (task) => <ProgressBar value={task.progress} />,
    },
    {
      key: 'status',
      label: 'Status',
      minWidth: 140,
      render: (task) => <StatusChip task={task} />,
    },
    {
      key: 'users',
      label: 'Assigned To',
      icon: PeopleIcon,
      minWidth: 140,
      render: (task) => (
        <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
          {task.users?.map(user => (
            <UserAvatar key={user.id} user={user} />
          ))}
        </AvatarGroup>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      minWidth: 140,
      render: (task) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          {!task.archived_at && <Tooltip title="View Task" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${task.id}`); }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>}
          {!task.archived_at && canEditTask(user, task) && (
            <Tooltip title="Edit Task" arrow>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); navigate(`/tasks/edit/${task.id}`); }}
                sx={{ color: '#f59e0b', borderRadius: '7px', transition: 'all 0.18s ease', '&:hover': { bgcolor: 'rgba(245,158,11,0.1)', color: '#d97706' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!task.archived_at && canArchiveTask(user, task) && (
            <Tooltip title="Archive task" arrow>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleArchiveClick(task); }}
                sx={{ color: '#ef4444', borderRadius: '7px', transition: 'all 0.18s ease', '&:hover': { bgcolor: 'rgba(239,68,68,0.1)', color: '#dc2626' } }}
              >
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {task.archived_at && (
            <Tooltip title="Restore Task" arrow>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleRestore(task); }}
                sx={{ color: theme.palette.success.main, borderRadius: '7px', '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.1) } }}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {(task.documentation_link || task.project_link) && (
            <>
              {task.documentation_link && (
                <Tooltip title="Open Documentation Link" arrow>
                  <IconButton
                    size="small"
                    component="a"
                    href={task.documentation_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: '#10b981', borderRadius: '7px', transition: 'all 0.18s ease', '&:hover': { bgcolor: 'rgba(16,185,129,0.1)', color: '#059669' } }}
                  >
                    <DescriptionIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {task.project_link && (
                <Tooltip title="Open Project Link" arrow>
                  <IconButton
                    size="small"
                    component="a"
                    href={task.project_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: '#10b981', borderRadius: '7px', transition: 'all 0.18s ease', '&:hover': { bgcolor: 'rgba(16,185,129,0.1)', color: '#059669' } }}
                  >
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      ),
    },
  ];

  // ── Render guards ──────────────────────────────────────────────────────────
  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="Please log in to view this page."
          severity="error"
          sx={{
            fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem',
            background: alpha('#450a0a', 0.9), color: '#fca5a5', border: '1px solid rgba(248,113,113,0.3)'
          }}
        />
      </Container>
    );
  }

  if (loading) return (
  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
    <Loading message="Loading tasks list" size={56} variant="primary" />
  </Box>
  );

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      {/* ── Header — same as UserList ──────────────────────────────────────── */}
      <Header
        title="Task Management"
        subtitle="Manage and track your team's tasks and progress"
        icon={TaskSidebarIcon}
        actionButton={(
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {[{ mode: 'table', Icon: ViewListIcon, label: 'List view' }, { mode: 'kanban', Icon: ViewKanbanIcon, label: 'Kanban view' }].map(({ mode, Icon, label }) => (
              <Tooltip key={mode} title={label} arrow>
                <IconButton
                  onClick={() => handleViewModeChange(mode)}
                  sx={{
                    borderRadius: '9px',
                    width: 36,
                    height: 36,
                    background: viewMode === mode ? alpha(ACCENT, 0.16) : alpha(TABLE_SURFACE_2, 0.5),
                    border: `1px solid ${alpha(ACCENT, viewMode === mode ? 0.4 : 0.15)}`,
                    color: viewMode === mode ? ACCENT : alpha(ACCENT, 0.5),
                    transition: 'all 0.2s ease',
                    '&:hover': { background: alpha(ACCENT, 0.1), borderColor: alpha(ACCENT, 0.35), color: ACCENT },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ))}
            {canCreateTask(user) && (
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => navigate('/tasks/create')}
                sx={{
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
                  fontSize: '0.875rem', borderRadius: '10px', px: 3, py: 1.5,
                  background: alpha(ACCENT, 0.16),
                  border: `1px solid ${alpha(ACCENT, 0.4)}`,
                  color: ACCENT,
                  transition: 'all 0.22s ease',
                  '&:hover': { background: alpha(ACCENT, 0.22), color: '#fff', transform: 'translateY(-1px)' },
                }}
              >
                Add Task
              </Button>
            )}
          </Box>
        )}
      />

      {/* ── Error message ─────────────────────────────────────────────────────── */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />
      
      {/* ── Success message ───────────────────────────────────────────────────── */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha(SUCCESS, 0.12),
          border: `1px solid ${alpha(SUCCESS, 0.25)}`,
          boxShadow: '0 4px 20px rgba(34,197,94,0.12)',
          color: alpha(SUCCESS, 0.95),
          mb: 3,
        }}
      />

      {/* ── Stat cards — same StatCards component as UserList ─────────────── */}
      <StatCards stats={statsData} compact onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterOptions}
        searchPlaceholder="Search by title, description, or assigned users..."
      />

      {/* ── TableList — same common component as UserList ─────────────────── */}
      {viewMode === 'table' && filters.record_state !== 'archived' && (
      <ListBulkToolbar
        config={TASK_BULK_CONFIG}
        currentUser={user}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: TASK_BULK_CONFIG.endpoint,
          setItems: setTasks,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'task(s)',
          refetch: fetchTasks,
          setError
        })}
        onDeleteSuccess={handleBulkArchive}
      />
      )}

      {viewMode === 'table' || filters.record_state === 'archived' ? (
      <TableList
        columns={columns}
        data={filteredTasks}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        {...(filters.record_state !== 'archived' ? bulkTableProps(TASK_BULK_CONFIG, user, selectedIds, setSelectedIds) : {})}
        loading={loading}
        emptyState={{
          icon: <AssignmentIcon />,
          title: 'No tasks found',
          description: searchTerm || Object.keys(filters).length > 0
            ? 'Try adjusting your filters to see more tasks.'
            : canCreateTask(user)
              ? 'Get started by creating your first task.'
              : 'No tasks to show.',
        }}
        onRowClick={(task) => { if (!task.archived_at) navigate(`/tasks/${task.id}`); }}
      />
      ) : (
        filteredTasks.length === 0 ? (
          <Box
            sx={{
              py: 8,
              textAlign: 'center',
              borderRadius: '14px',
              border: '1px dashed rgba(148,163,184,0.25)',
              bgcolor: 'rgba(15,23,42,0.35)',
            }}
          >
            <AssignmentIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)', mb: 1 }} />
            <Typography sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>No tasks found</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem' }}>
              {searchTerm || Object.keys(filters).length > 0
                ? 'Try adjusting your filters.'
                : 'Create a task or switch to table view.'}
            </Typography>
          </Box>
        ) : (
          <TaskKanban
            tasks={filteredTasks}
            user={user}
            updatingTaskId={updatingTaskId}
            onStatusChange={handleKanbanStatusChange}
            onOpenTask={(task) => navigate(`/tasks/${task.id}`)}
            onEditTask={(task) => navigate(`/tasks/edit/${task.id}`)}
            onArchiveTask={handleArchiveClick}
          />
        )
      )}

      {/* The shared confirmation dialog supports task-specific archive wording. */}
      <ConfirmDeleteDialog
        open={archiveDialogOpen}
        onClose={() => { setArchiveDialogOpen(false); setTaskToArchive(null); }}
        onConfirm={handleArchiveConfirm}
        loading={archiveLoading}
        itemType="task"
        itemName={taskToArchive?.title}
        title="Confirm Archive"
        description="The task will be archived (soft-deleted). Administrators can restore it from the database if needed."
        confirmLabel="Archive task"
      />

    </Container>
  );
};

export default TaskList;
